/*
 * dd-moq-guard.js — Minimum Order Quantity in the cart drawer.
 * -----------------------------------------------------------------------------
 * WHY
 * The MOQ lives in the product metafield custom.minimum_quantity_required. The
 * product page respects it, but the cart drawer customers actually see is
 * GoKwik's KwikCart (a Svelte app inside <shadow-dom-container>'s shadow root;
 * the theme's own <cart-drawer> is hidden by GoKwik and only shown to B2B
 * customers). KwikCart knows nothing about the metafield, so its "-" button
 * happily took an MOQ-6 product down to 5, 4, 3 …
 *
 * WHAT IT DOES
 *   1. Drawer UI — for a line whose product has an MOQ, the "-" button is shown
 *      disabled and its click is swallowed (capture phase, before KwikCart's own
 *      handler) once the quantity is at or below the MOQ. "+" and the remove
 *      (bin) button are untouched. A typed value below the MOQ is put back.
 *   2. Safety net — any /cart/change or /cart/update request (fetch or XHR,
 *      from any script) that would lower an MOQ product to a quantity between
 *      1 and its MOQ is rewritten to keep the MOQ. Removing the line (0) and
 *      increasing are always allowed. This also covers rows the UI layer could
 *      not match and the B2B theme drawer / cart page.
 *
 *   Products without an MOQ (missing, 0 or 1) are never touched.
 *
 * DATA
 *   window.ddMoqSeed  — { productId: moq } rendered with the page
 *                       (snippets/dd-moq-guard.liquid → dd-moq-map.liquid)
 *   /?sections=dd-cart-moq — same map, fetched when a product we have not seen
 *                       lands in the cart (sections/dd-cart-moq.liquid)
 *   Cart contents come from window.ddCart (assets/dd-cart-state.js).
 *
 * Debug: window.ddMoqGuard.debug()
 * -----------------------------------------------------------------------------
 */
(function () {
  'use strict';
  if (window.__ddMoqGuardInit) return;
  window.__ddMoqGuardInit = true;

  var HOST_SELECTOR = 'shadow-dom-container';
  var SECTION_ID = 'dd-cart-moq';
  var CART_WRITE_RE = /\/cart\/(change|update)(\.js)?(\?|#|$)/;

  /* product_id (string) -> MOQ (number). 0/1 = no MOQ. */
  var moqMap = Object.create(null);
  var seed = window.ddMoqSeed || {};
  Object.keys(seed).forEach(function (k) { moqMap[k] = parseInt(seed[k], 10) || 0; });

  var cart = null;

  function moqFor(productId) {
    var m = moqMap[String(productId)];
    return m > 1 ? m : 0;
  }

  /*
   * The one rule. Given a cart line and a requested quantity, return the
   * quantity that is allowed.
   *   - no MOQ, removal (0) or not a number  -> as requested
   *   - at/above MOQ                          -> as requested
   *   - below MOQ but an increase             -> as requested (line was already
   *                                              below MOQ, e.g. added elsewhere)
   *   - below MOQ and a decrease              -> keep MOQ (or the current qty if
   *                                              that is already lower)
   */
  function allowedQty(item, requested) {
    var m = item ? moqFor(item.product_id) : 0;
    var q = parseInt(requested, 10);
    if (!m || isNaN(q) || q <= 0 || q >= m) return requested;
    if (q >= item.quantity) return requested;
    return Math.min(item.quantity, m);
  }

  /* ---------------------------------------------------------------- cart -- */

  function setCart(c) {
    if (!c || !c.items) return;
    cart = c;
    loadMissingMoq();
    scheduleRender();
  }

  function readCart() {
    if (window.ddCart && window.ddCart.get) return window.ddCart.get().then(setCart);
    return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(setCart)
      .catch(function () {});
  }

  if (window.ddCart && window.ddCart.subscribe) window.ddCart.subscribe(setCart);
  readCart();

  /* Fetch MOQ for cart products we have no value for yet. */
  var moqLoading = false;
  var moqFailures = 0;

  function loadMissingMoq() {
    if (!cart || moqLoading || moqFailures > 3) return;
    var missing = cart.items.some(function (i) { return !(String(i.product_id) in moqMap); });
    if (!missing) return;

    moqLoading = true;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    /* Path deliberately has no "/cart" in it so no other script's fetch patch
       mistakes this read for a cart call. */
    fetch(root + '?sections=' + SECTION_ID, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(function (json) {
        var html = json && json[SECTION_ID];
        var m = html && html.match(/<script[^>]*id="dd-cart-moq-data"[^>]*>([\s\S]*?)<\/script>/);
        var data = m ? JSON.parse(m[1]) : null;
        if (!data) throw new Error('no data');
        Object.keys(data).forEach(function (k) { moqMap[k] = parseInt(data[k], 10) || 0; });
        moqFailures = 0;
      })
      .catch(function () { moqFailures++; })
      .then(function () {
        moqLoading = false;
        scheduleRender();
        /* The cart may have changed while we were fetching. */
        if (cart && cart.items.some(function (i) { return !(String(i.product_id) in moqMap); })) {
          setTimeout(loadMissingMoq, 1000 * (moqFailures + 1));
        }
      });
  }

  /* ------------------------------------------------- network safety net -- */

  function findLine(ref, byLine) {
    if (!cart) return null;
    if (byLine) return cart.items[parseInt(ref, 10) - 1] || null;
    var s = String(ref);
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (s.indexOf(':') !== -1 ? it.key === s : String(it.variant_id) === s || it.key === s) return it;
    }
    return null;
  }

  /* Rewrite a plain object payload in place. Returns true when changed. */
  function guardPayload(isChange, data) {
    var changed = false;
    if (isChange) {
      if (data.quantity == null) return false;
      var item = data.line != null ? findLine(data.line, true) : findLine(data.id, false);
      var q = allowedQty(item, data.quantity);
      if (String(q) !== String(data.quantity)) { data.quantity = q; changed = true; }
      return changed;
    }
    var updates = data.updates;
    if (!updates || typeof updates !== 'object') return false;
    if (Array.isArray(updates)) {
      updates.forEach(function (v, idx) {
        var q2 = allowedQty(findLine(idx + 1, true), v);
        if (String(q2) !== String(v)) { updates[idx] = q2; changed = true; }
      });
    } else {
      Object.keys(updates).forEach(function (k) {
        var q3 = allowedQty(findLine(k, false), updates[k]);
        if (String(q3) !== String(updates[k])) { updates[k] = q3; changed = true; }
      });
    }
    return changed;
  }

  /* Same, for URLSearchParams / FormData style bodies. */
  function guardParams(isChange, params) {
    var changed = false;
    if (isChange) {
      var obj = { quantity: params.get('quantity'), line: params.get('line'), id: params.get('id') };
      if (obj.line == null) delete obj.line;
      if (obj.quantity != null && guardPayload(true, obj)) { params.set('quantity', obj.quantity); changed = true; }
      return changed;
    }
    var arrIdx = 0;
    var entries = [];
    params.forEach(function (v, k) { entries.push([k, v]); });
    entries.forEach(function (e) {
      var k = e[0];
      var m = k.match(/^updates\[(.*)\]$/);
      if (!m) return;
      var item = m[1] === '' ? findLine(++arrIdx, true) : findLine(m[1], false);
      var q = allowedQty(item, e[1]);
      if (String(q) !== String(e[1])) changed = true;
      e[1] = String(q);
    });
    if (changed) {
      entries.forEach(function (e) { if (/^updates\[/.test(e[0])) params.delete(e[0]); });
      entries.forEach(function (e) { if (/^updates\[/.test(e[0])) params.append(e[0], e[1]); });
    }
    return changed;
  }

  function guardBody(url, body) {
    if (!cart || body == null) return body;
    var isChange = /\/cart\/change/.test(url);
    var changed = false;
    try {
      if (typeof body === 'string') {
        var t = body.trim();
        if (t.charAt(0) === '{') {
          var data = JSON.parse(t);
          if (guardPayload(isChange, data)) { body = JSON.stringify(data); changed = true; }
        } else {
          var p = new URLSearchParams(t);
          if (guardParams(isChange, p)) { body = p.toString(); changed = true; }
        }
      } else if ((window.URLSearchParams && body instanceof URLSearchParams) ||
                 (window.FormData && body instanceof FormData)) {
        changed = guardParams(isChange, body);
      }
    } catch (e) { /* unknown body shape — leave it alone */ }
    if (changed) {
      scheduleRender();
      try { document.dispatchEvent(new CustomEvent('dd:moq-enforced', { detail: { url: url } })); } catch (e2) {}
    }
    return body;
  }

  /* XHR — KwikCart uses this for /cart/change.js */
  var XHR = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
  if (XHR && !XHR.__ddMoqPatched) {
    var xOpen = XHR.open;
    var xSend = XHR.send;
    XHR.open = function (method, url) {
      try { this.__ddMoqUrl = String(url || ''); } catch (e) {}
      return xOpen.apply(this, arguments);
    };
    XHR.send = function (body) {
      var b = body;
      try { if (this.__ddMoqUrl && CART_WRITE_RE.test(this.__ddMoqUrl)) b = guardBody(this.__ddMoqUrl, body); } catch (e) {}
      return arguments.length ? xSend.call(this, b) : xSend.call(this);
    };
    XHR.__ddMoqPatched = true;
  }

  /* fetch — theme cart.js / cart-drawer.js / cart page */
  if (typeof window.fetch === 'function' && !window.fetch.__ddMoqPatched) {
    var prevFetch = window.fetch;
    var guardedFetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || String(input || '');
        if (init && init.body != null && CART_WRITE_RE.test(url)) {
          var nb = guardBody(url, init.body);
          if (nb !== init.body) {
            var copy = {};
            for (var k in init) copy[k] = init[k];
            copy.body = nb;
            var args = Array.prototype.slice.call(arguments);
            args[1] = copy;
            return prevFetch.apply(window, args);
          }
        }
      } catch (e) {}
      return prevFetch.apply(window, arguments);
    };
    /* keep flags other scripts set on their own wrappers */
    Object.keys(prevFetch).forEach(function (k) { try { guardedFetch[k] = prevFetch[k]; } catch (e) {} });
    guardedFetch.__ddMoqPatched = true;
    window.fetch = guardedFetch;
  }

  /* --------------------------------------------- GoKwik drawer (UI layer) -- */

  var shadow = null;
  var renderTimer = null;

  var CSS = '' +
    '.reduce-quantity[data-dd-moq-locked]{opacity:.35 !important;cursor:not-allowed !important;}' +
    '.dd-moq-hint{position:absolute;right:8px;bottom:6px;z-index:5;max-width:80%;' +
      'background:#1d1d1d;color:#fff;font-size:11px;line-height:1.3;padding:5px 8px;border-radius:4px;' +
      'box-shadow:0 2px 6px rgba(0,0,0,.2);pointer-events:none;opacity:0;transform:translateY(4px);' +
      'transition:opacity .15s ease,transform .15s ease;}' +
    '.dd-moq-hint.is-visible{opacity:1;transform:none;}';

  function norm(t) {
    return String(t || '').replace(/&amp;/g, '&').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  }

  /* Match a drawer row to a cart line by title (KwikCart rows carry no ids).
     MOQ is per product, so lines of the same product share it — a title match
     is all we need. */
  function lineForTile(tile) {
    if (!cart) return null;
    var el = tile.querySelector('.sidecart-product-title');
    var tt = el ? norm(el.textContent) : '';
    if (!tt) return null;
    var i, it, pt;
    for (i = 0; i < cart.items.length; i++) {
      it = cart.items[i];
      if (norm(it.product_title) === tt || norm(it.title) === tt) return it;
    }
    for (i = 0; i < cart.items.length; i++) {
      it = cart.items[i];
      pt = norm(it.product_title);
      if (pt && (pt.indexOf(tt) === 0 || tt.indexOf(pt) === 0)) return it;
    }
    return null;
  }

  function tileOf(el) {
    return el.closest('.product-tile-wrapper') || el.closest('.item');
  }

  function qtyInput(tile) {
    return tile.querySelector('input.item-quantity') || tile.querySelector('.quantity-handler input');
  }

  function shownQty(tile, item) {
    var input = qtyInput(tile);
    var v = input ? parseInt(input.value, 10) : NaN;
    return isNaN(v) ? (item ? item.quantity : NaN) : v;
  }

  function render() {
    renderTimer = null;
    if (!shadow) return;
    var tiles = shadow.querySelectorAll('.product-tile-wrapper');
    for (var i = 0; i < tiles.length; i++) {
      var tile = tiles[i];
      var minus = tile.querySelector('.reduce-quantity');
      if (!minus) continue;
      var item = lineForTile(tile);
      var m = item ? moqFor(item.product_id) : 0;
      var locked = m > 1 && shownQty(tile, item) <= m;
      if (locked) {
        if (!minus.hasAttribute('data-dd-moq-locked')) {
          minus.setAttribute('data-dd-moq-locked', '');
          minus.setAttribute('aria-disabled', 'true');
          minus.setAttribute('title', 'Minimum order quantity is ' + m);
        }
      } else if (minus.hasAttribute('data-dd-moq-locked')) {
        minus.removeAttribute('data-dd-moq-locked');
        minus.removeAttribute('aria-disabled');
        minus.removeAttribute('title');
      }
    }
  }

  function scheduleRender(delay) {
    if (!shadow) return;
    clearTimeout(renderTimer);
    renderTimer = setTimeout(render, delay == null ? 60 : delay);
  }

  function renderSoon() {
    scheduleRender(60);
    setTimeout(render, 700);
    setTimeout(render, 1600);
  }

  function flashHint(tile, m) {
    var hint = tile.querySelector('.dd-moq-hint');
    if (!hint) {
      hint = document.createElement('div');
      hint.className = 'dd-moq-hint';
      hint.setAttribute('role', 'status');
      if (getComputedStyle(tile).position === 'static') tile.style.position = 'relative';
      tile.appendChild(hint);
    }
    hint.textContent = 'Minimum order quantity for this product is ' + m;
    /* restart the fade */
    hint.classList.remove('is-visible');
    void hint.offsetWidth;
    hint.classList.add('is-visible');
    clearTimeout(hint.__t);
    hint.__t = setTimeout(function () { hint.classList.remove('is-visible'); }, 2200);
  }

  function onShadowClick(e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var minus = t.closest('.reduce-quantity');
    if (!minus) {
      if (t.closest('.increase-quantity, .remove-btn')) renderSoon();
      return;
    }
    var tile = tileOf(minus);
    var item = tile ? lineForTile(tile) : null;
    var m = item ? moqFor(item.product_id) : 0;
    if (!m || !(shownQty(tile, item) <= m)) { renderSoon(); return; }

    /* At (or below) MOQ: the "-" does nothing. */
    e.preventDefault();
    e.stopImmediatePropagation();
    flashHint(tile, m);
    render();
  }

  /* Typed quantity: put back a value below the MOQ before KwikCart reads it. */
  function onShadowFieldCommit(e) {
    var input = e.target;
    if (!input || !input.matches || !input.matches('input.item-quantity, .quantity-handler input')) return;
    if (e.type === 'keydown' && e.key !== 'Enter') return;
    var tile = tileOf(input);
    var item = tile ? lineForTile(tile) : null;
    if (!item || !moqFor(item.product_id)) return;
    var v = parseInt(input.value, 10);
    var ok = allowedQty(item, v);
    if (!isNaN(v) && ok !== v) {
      input.value = ok;
      flashHint(tile, moqFor(item.product_id));
    }
    renderSoon();
  }

  function attach() {
    var host = document.querySelector(HOST_SELECTOR);
    var root = host && host.shadowRoot;
    if (!root || root === shadow) return !!shadow;
    shadow = root;

    if (!root.getElementById('dd-moq-style')) {
      var st = document.createElement('style');
      st.id = 'dd-moq-style';
      st.textContent = CSS;
      root.appendChild(st);
    }
    root.addEventListener('click', onShadowClick, true);
    root.addEventListener('change', onShadowFieldCommit, true);
    root.addEventListener('blur', onShadowFieldCommit, true); /* capture: blur doesn't bubble */
    root.addEventListener('keydown', onShadowFieldCommit, true);
    root.addEventListener('input', function () { scheduleRender(120); }, true);

    /* KwikCart re-renders rows on every cart change. */
    new MutationObserver(function () { scheduleRender(80); }).observe(root, { childList: true, subtree: true });
    readCart();
    scheduleRender(0);
    return true;
  }

  /* The host is added to <body> by GoKwik, sometimes only when the cart opens. */
  function watchForHost() {
    if (attach()) return;
    var mo = new MutationObserver(function () { if (attach()) mo.disconnect(); });
    mo.observe(document.body, { childList: true });
    /* belt and braces: the shadow root can be attached after the element */
    var tries = 0;
    var iv = setInterval(function () { if (attach() || ++tries > 40) clearInterval(iv); }, 1500);
  }

  if (document.body) watchForHost();
  else document.addEventListener('DOMContentLoaded', watchForHost);

  window.ddMoqGuard = {
    moqFor: moqFor,
    allowedQty: allowedQty,
    debug: function () {
      return {
        moq: Object.assign({}, moqMap),
        cart: cart && cart.items.map(function (i) {
          return { title: i.product_title, product_id: i.product_id, qty: i.quantity, moq: moqFor(i.product_id) };
        }),
        drawerAttached: !!shadow
      };
    }
  };
})();
